import type { ExecutionStatus } from "@/api/model/irrigation-plan-execution-report";
import { dayjs } from "@/utils/date";

import { STATUS_CONFIG } from "./constants";
import type { ReportItem } from "./types";

/**
 * Get status configuration for a given execution status
 */
export function getStatusConfig(status: ExecutionStatus) {
  return STATUS_CONFIG[status] ?? STATUS_CONFIG.TIMED_OUT;
}

/**
 * Get problem indicators for a report item
 */
export function getProblemIndicators(item: ReportItem): { label: string; type: string }[] {
  const indicators = [];

  if (item.status === "MISSED") {
    indicators.push({ label: "Execução perdida", type: "error" });
  }

  if (item.status === "TIMED_OUT") {
    indicators.push({ label: "Execução incompleta", type: "warning" });
  }

  if (item.missed_irrigation_steps.length > 0) {
    indicators.push({ label: `${item.missed_irrigation_steps.length} setores não irrigados`, type: "error" });
  }

  if (item.uses_ferti && item.missed_fertigation_steps.length > 0) {
    indicators.push({ label: `${item.missed_fertigation_steps.length} setores sem fertirrigação`, type: "warning" });
  }

  if (item.uses_waterpump && !item.waterpump_working) {
    indicators.push({ label: "Bomba não funcionou", type: "error" });
  }

  return indicators;
}

/**
 * Get the effective status for a report item, considering problems
 */
export function getEffectiveStatus(item: ReportItem): ExecutionStatus | "PROBLEMAS" {
  // If status is RUNNING, do not report as "PROBLEMAS" even if there are missed steps yet
  if (item.status === "RUNNING") {
    return item.status;
  }

  // Check if item has problems but is not already in a terminal state
  if (
    (item.status === "COMPLETED" &&
      item.missed_irrigation_steps.length === 0 &&
      item.missed_fertigation_steps.length === 0) ||
    item.status === "TIMED_OUT" ||
    item.status === "MISSED"
  ) {
    return item.status;
  }

  const problems = getProblemIndicators(item);
  if (problems.length > 0) {
    return "PROBLEMAS";
  }

  return item.status;
}

/**
 * Get the effective status configuration for a report item
 */
export function getEffectiveStatusConfig(item: ReportItem) {
  const effectiveStatus = getEffectiveStatus(item);
  return STATUS_CONFIG[effectiveStatus] ?? STATUS_CONFIG.TIMED_OUT;
}

/**
 * Format a date-time string for display
 */
export function formatDateTime(isoString: string | null): string {
  if (!isoString) return "-";
  return dayjs(isoString).format("DD/MM/YY HH:mm");
}
